// src/pages/workflows/WorkflowTemplateForm.jsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Button,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Divider,
  Card,
  CardContent,
  CardHeader,
  CardActions,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  Autocomplete,
  IconButton,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Breadcrumbs,
  Link,
  Switch,
  FormControlLabel,
  Tooltip,
  useTheme,
  alpha,
} from '@mui/material';
import {
  Add as AddIcon,
  Save as SaveIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  ArrowUpward as MoveUpIcon,
  ArrowDownward as MoveDownIcon,
  DragIndicator as DragIcon,
  Person as PersonIcon,
  Description as DescriptionIcon,
  ArrowBack as BackIcon,
  NavigateNext as NextIcon,
} from '@mui/icons-material';

// Import custom components
import { MagicLoader } from '../../design-system';
import ConfirmDialog from '../../components/ui/ConfirmDialog';

// Import hooks and stores
import useWorkflowStore from '../../stores/workflowStore';
import useNotificationStore from '../../stores/notificationStore';
import useAuthStore from '../../stores/authStore';
import useUserStore from '../../stores/userStore';

// Template types
const TEMPLATE_TYPES = [
  { value: 'contentApproval', label: 'Content Approval' },
  { value: 'campaignApproval', label: 'Campaign Approval' },
  { value: 'budgetApproval', label: 'Budget Approval' },
  { value: 'clientOnboarding', label: 'Client Onboarding' },
  { value: 'invoiceApproval', label: 'Invoice Approval' },
  { value: 'userAccess', label: 'User Access' },
  { value: 'custom', label: 'Custom Workflow' },
];

// Resource types
const RESOURCE_TYPES = [
  { value: 'content', label: 'Content' },
  { value: 'campaign', label: 'Campaign' },
  { value: 'client', label: 'Client' },
  { value: 'brand', label: 'Brand' }, 
  { value: 'invoice', label: 'Invoice' },
  { value: 'user', label: 'User' },
  { value: 'other', label: 'Other' }
];

// Mock user data for step assignees
// In a real app, you would fetch these from the user service
// const SAMPLE_USERS = [
//   { _id: 'user1', name: 'John Smith', role: 'manager' },
//   { _id: 'user2', name: 'Emma Wilson', role: 'content_creator' },
//   { _id: 'user3', name: 'Michael Brown', role: 'approver' },
//   { _id: 'user4', name: 'Sarah Johnson', role: 'client' },
// ];

const WorkflowTemplateForm = () => {
  const { id } = useParams();
  const theme = useTheme();
  const navigate = useNavigate();
  
  // Determine if this is edit mode
  const isEditMode = !!id;
  
  // Store hooks
  const { 
    currentTemplate, 
    fetchTemplateById,
    createTemplate,
    updateTemplate,
    isLoading, 
    error,
    clearError
  } = useWorkflowStore();
  
  const { showSuccess, showError } = useNotificationStore();
  const {fetchUsers, users} = useUserStore();
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    type: 'contentApproval',
    resourceType: 'content', // Added required resourceType field
    status: 'draft',
    steps: [],
  });
  
  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [stepDialogOpen, setStepDialogOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState(null);
  const [currentStepIndex, setCurrentStepIndex] = useState(-1);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [stepToDelete, setStepToDelete] = useState(null);
  
  // Load template data if in edit mode
  useEffect(() => {
    if (isEditMode) {
      const loadTemplate = async () => {
        try {
          const template = await fetchTemplateById(id);
          if (template) {
            setFormData({
              name: template.name || '',
              description: template.description || '',
              type: template.type || 'contentApproval',
              resourceType: template.resourceType || 'content',
              status: template.status || 'draft',
              steps: template.steps || [],
            });
          }
        } catch (err) {
          showError(err.message || 'Failed to load workflow template');
        }
      };
      
      loadTemplate();
    }
    
    // Cleanup
    return () => {
      clearError();
    };
  }, [fetchTemplateById, id, isEditMode, clearError]);
  
  // Handle form field changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    
    setFormData({
      ...formData,
      [name]: value
    });
    
    // Clear any error for this field
    if (formErrors[name]) {
      setFormErrors({
        ...formErrors,
        [name]: null
      });
    }
  };
  
  // Handle status toggle
  const handleStatusToggle = (e) => {
    setFormData({
      ...formData,
      status: e.target.checked ? 'active' : 'draft'
    });
  };
  
  // Step dialog functions
  const openAddStepDialog = () => {
    setCurrentStep({
      name: '',
      description: '',
      assignedTo: null,
      dueTimeframe: 24,
    });
    setCurrentStepIndex(-1);
    setStepDialogOpen(true);
  };
  
  const openEditStepDialog = (step, index) => {
    setCurrentStep({ ...step });
    setCurrentStepIndex(index);
    setStepDialogOpen(true);
  };
  
  const handleStepDialogClose = () => {
    setStepDialogOpen(false);
    setCurrentStep(null);
    setCurrentStepIndex(-1);
  };
  
  const validateStepForm = () => {
    const errors = {};
    
    if (!currentStep.name.trim()) {
      errors.name = 'Step name is required';
    }
    
    if (!currentStep.assignedTo) {
      errors.assignedTo = 'Please assign this step to a user or role';
    }
    
    return errors;
  };
  
  const handleStepSave = () => {
    // Validate step form
    const stepErrors = validateStepForm();
    
    if (Object.keys(stepErrors).length > 0) {
      setFormErrors(stepErrors);
      return;
    }
    
    // Create a copy of the steps array
    const newSteps = [...formData.steps];
    
    if (currentStepIndex >= 0) {
      // Update existing step with order preserved
      newSteps[currentStepIndex] = {
        ...currentStep,
        order: currentStepIndex + 1 // Ensure order is set to index + 1
      };
    } else {
      // Add new step with next order number
      newSteps.push({
        ...currentStep,
        order: newSteps.length + 1 // New step gets next available order number
      });
    }
    
    // Update form data
    setFormData({
      ...formData,
      steps: newSteps
    });
    
    // Close dialog
    handleStepDialogClose();
  };
  
  // Step deletion functions
  const openDeleteStepDialog = (index) => {
    setStepToDelete(index);
    setDeleteDialogOpen(true);
  };
  
  const handleDeleteStep = () => {
    if (stepToDelete === null) return;
    
    // Create a copy of the steps array without the deleted step
    const newSteps = formData.steps.filter((_, index) => index !== stepToDelete);
    
    // Update the order property for all steps after deletion
    const updatedSteps = newSteps.map((step, index) => ({
      ...step,
      order: index + 1 // Reassign order after removal
    }));
    
    // Update form data
    setFormData({
      ...formData,
      steps: updatedSteps
    });
    
    // Close dialog
    setDeleteDialogOpen(false);
    setStepToDelete(null);
  };
  
  // Step reordering functions
  const moveStepUp = (index) => {
    if (index <= 0) return;
    
    const newSteps = [...formData.steps];
    
    // Swap the steps
    const temp = newSteps[index];
    newSteps[index] = newSteps[index - 1];
    newSteps[index - 1] = temp;
    
    // Update the order properties to match their new positions
    newSteps[index].order = index + 1;
    newSteps[index - 1].order = index;
    
    setFormData({
      ...formData,
      steps: newSteps
    });
  };
  
  const moveStepDown = (index) => {
    if (index >= formData.steps.length - 1) return;
    
    const newSteps = [...formData.steps];
    
    // Swap the steps
    const temp = newSteps[index];
    newSteps[index] = newSteps[index + 1];
    newSteps[index + 1] = temp;
    
    // Update the order properties to match their new positions
    newSteps[index].order = index + 1;
    newSteps[index + 1].order = index + 2;
    
    setFormData({
      ...formData,
      steps: newSteps
    });
  };
  
  // Validate the full form
  const validateForm = () => {
    const errors = {};
    
    if (!formData.name.trim()) {
      errors.name = 'Template name is required';
    }
    
    if (!formData.type) {
      errors.type = 'Please select a workflow type';
    }
    
    if (!formData.resourceType) {
      errors.resourceType = 'Resource type is required';
    }
    
    if (formData.steps.length === 0) {
      errors.steps = 'At least one step is required for the workflow';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };
  
  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form
    if (!validateForm()) {
      return;
    }
    
    // Final check to ensure all steps have the correct order property
    const stepsWithCorrectOrder = formData.steps.map((step, index) => ({
      ...step,
      order: index + 1
    }));
    
    const finalFormData = {
      ...formData,
      steps: stepsWithCorrectOrder
    };
    
    setIsSubmitting(true);
    
    try {
      if (isEditMode) {
        // Update template
        await updateTemplate(id, finalFormData);
        showSuccess('Workflow template updated successfully');
      } else {
        // Create template
        const newTemplate = await createTemplate(finalFormData);
        showSuccess('Workflow template created successfully');
        navigate(`/workflows/templates/${newTemplate._id}`);
      }
      
      // Navigate back to templates list
      if (isEditMode) {
        navigate('/workflows/templates');
      }
    } catch (err) {
      showError(err.message || 'Failed to save workflow template');
      setIsSubmitting(false);
    }
  };
  
  // Find template type label
  const getTemplateTypeLabel = (value) => {
    const type = TEMPLATE_TYPES.find(t => t.value === value);
    return type ? type.label : value;
  };
  
  // Loading state
  if (isLoading && isEditMode && !currentTemplate) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <MagicLoader variant="magical" size="large" message="Loading template data..." />
      </Box>
    );
  }
  
  return (
    <Box sx={{ py: 3 }}>
      {/* Breadcrumbs navigation */}
      <Breadcrumbs 
        separator={<NextIcon fontSize="small" />} 
        aria-label="breadcrumb"
        sx={{ mb: 2 }}
      >
        <Link 
          component={Link} 
          to="/workflows/templates" 
          underline="hover" 
          color="inherit"
        >
          Workflow Templates
        </Link>
        <Typography color="text.primary">
          {isEditMode ? `Edit ${formData.name}` : 'Create Template'}
        </Typography>
      </Breadcrumbs>
      
      <Typography variant="h5" component="h1" gutterBottom>
        {isEditMode ? `Edit Workflow Template` : 'Create Workflow Template'}
      </Typography>
      
      <Paper sx={{ p: 3, mt: 3 }}>
        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            {/* Template Name */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                id="name"
                name="name"
                label="Template Name"
                value={formData.name}
                onChange={handleChange}
                error={!!formErrors.name}
                helperText={formErrors.name}
                required
              />
            </Grid>
            
            {/* Template Type */}
            <Grid item xs={12} md={6}>
              <FormControl fullWidth error={!!formErrors.type}>
                <InputLabel id="type-label">Workflow Type</InputLabel>
                <Select
                  labelId="type-label"
                  id="type"
                  name="type"
                  value={formData.type}
                  onChange={handleChange}
                  label="Workflow Type"
                >
                  {TEMPLATE_TYPES.map(type => (
                    <MenuItem key={type.value} value={type.value}>
                      {type.label}
                    </MenuItem>
                  ))}
                </Select>
                {formErrors.type && (
                  <FormHelperText>{formErrors.type}</FormHelperText>
                )}
              </FormControl>
            </Grid>
            
            {/* Resource Type */}
            <Grid item xs={12} md={6}>
              <FormControl fullWidth error={!!formErrors.resourceType}>
                <InputLabel id="resourceType-label">Resource Type</InputLabel>
                <Select
                  labelId="resourceType-label"
                  id="resourceType"
                  name="resourceType"
                  value={formData.resourceType}
                  onChange={handleChange}
                  label="Resource Type"
                  required
                >
                  {RESOURCE_TYPES.map(type => (
                    <MenuItem key={type.value} value={type.value}>
                      {type.label}
                    </MenuItem>
                  ))}
                </Select>
                {formErrors.resourceType && (
                  <FormHelperText>{formErrors.resourceType}</FormHelperText>
                )}
              </FormControl>
            </Grid>
            
            {/* Template Description */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                id="description"
                name="description"
                label="Description"
                value={formData.description}
                onChange={handleChange}
                multiline
                rows={2}
              />
            </Grid>
            
            {/* Template Status */}
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.status === 'active'}
                    onChange={handleStatusToggle}
                    
                  />
                }
                label={
                  <Typography variant="body1">
                    {formData.status === 'active' ? 'Active' : 'Draft'} 
                    <Typography variant="caption" sx={{ ml: 1, color: 'text.secondary' }}>
                      ({formData.status === 'active' ? 'Template is available for use' : 'Template is not available for use'})
                    </Typography>
                  </Typography>
                }
              />
            </Grid>
          </Grid>
          
          <Divider sx={{ my: 3 }} />
          
          {/* Workflow Steps */}
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">
                Workflow Steps
              </Typography>
              <Button
                variant="outlined"
                
                startIcon={<AddIcon />}
                onClick={openAddStepDialog}
              >
                Add Step
              </Button>
            </Box>
            
            {formErrors.steps && (
              <Typography color="error" variant="body2" sx={{ mb: 2 }}>
                {formErrors.steps}
              </Typography>
            )}
            
            {formData.steps.length > 0 ? (
              <Stepper orientation="vertical">
                {formData.steps.map((step, index) => (
                  <Step key={index} active={true}>
                    <StepLabel>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Typography variant="subtitle1">{step.name}</Typography>
                        <Box>
                          <Tooltip title="Move up">
                            <span>
                              <IconButton
                                size="small"
                                onClick={() => moveStepUp(index)}
                                disabled={index === 0}
                              >
                                <MoveUpIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                          
                          <Tooltip title="Move down">
                            <span>
                              <IconButton
                                size="small"
                                onClick={() => moveStepDown(index)}
                                disabled={index === formData.steps.length - 1}
                              >
                                <MoveDownIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                          
                          <Tooltip title="Edit step">
                            <IconButton
                              size="small"
                              onClick={() => openEditStepDialog(step, index)}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          
                          <Tooltip title="Delete step">
                            <IconButton
                              size="small"
                              onClick={() => openDeleteStepDialog(index)}
                              color="error"
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </Box>
                    </StepLabel>
                    <StepContent>
                      <Box sx={{ ml: 2 }}>
                        {step.description && (
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            {step.description}
                          </Typography>
                        )}
                        
                        <Typography variant="body2">
                          <strong>Assigned to:</strong> {step.assignedTo?.name || 'Unassigned'}
                        </Typography>
                        
                        {step.dueTimeframe && (
                          <Typography variant="body2">
                            <strong>Due timeframe:</strong> {step.dueTimeframe} hours
                          </Typography>
                        )}
                        
                        <Typography variant="body2">
                          <strong>Order:</strong> {step.order || index + 1}
                        </Typography>
                      </Box>
                    </StepContent>
                  </Step>
                ))}
              </Stepper>
            ) : (
              <Paper 
                variant="outlined" 
                sx={{ 
                  p: 3, 
                  textAlign: 'center',
                  bgcolor: alpha(theme.palette.background.default, 0.5)
                }}
              >
                <Typography color="text.secondary">
                  No steps added yet. Click "Add Step" to define workflow steps.
                </Typography>
              </Paper>
            )}
          </Box>
          
          {/* Form Actions */}
          <Box sx={{ mt: 4, display: 'flex', justifyContent: 'space-between' }}>
            <Button
              variant="outlined"
              startIcon={<BackIcon />}
              onClick={() => navigate('/workflows/templates')}
            >
              Cancel
            </Button>
            
            <Button
              type="submit"
              variant="contained"
              
              startIcon={<SaveIcon />}
              loading={isSubmitting}
              disabled={isSubmitting}
              magical={true}
            >
              {isEditMode ? 'Update Template' : 'Create Template'}
            </Button>
          </Box>
        </form>
      </Paper>
      
      {/* Step Dialog */}
      <Dialog
        open={stepDialogOpen}
        onClose={handleStepDialogClose}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {currentStepIndex >= 0 ? 'Edit Workflow Step' : 'Add Workflow Step'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 0 }}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Step Name"
                value={currentStep?.name || ''}
                onChange={(e) => setCurrentStep({...currentStep, name: e.target.value})}
                error={!!formErrors.name}
                helperText={formErrors.name}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Autocomplete
                options={users}
                getOptionLabel={(option) => option?.name}
                value={currentStep?.assignedTo}
                onChange={(e, value) => setCurrentStep({...currentStep, assignedTo: value})}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Assign To"
                    required
                    error={!!formErrors.assignedTo}
                    helperText={formErrors.assignedTo}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Step Description"
                value={currentStep?.description || ''}
                onChange={(e) => setCurrentStep({...currentStep, description: e.target.value})}
                multiline
                rows={2}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Due Timeframe (hours)"
                type="number"
                value={currentStep?.dueTimeframe || 24}
                onChange={(e) => setCurrentStep({...currentStep, dueTimeframe: parseInt(e.target.value) || 24})}
                inputProps={{ min: 1 }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleStepDialogClose}>Cancel</Button>
          <Button
            onClick={handleStepSave}
            variant="contained"
            
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Delete Step Confirmation Dialog */}
      <ConfirmDialog
        open={deleteDialogOpen}
        title="Delete Step"
        content="Are you sure you want to delete this workflow step? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={handleDeleteStep}
        onCancel={() => {
          setDeleteDialogOpen(false);
          setStepToDelete(null);
        }}
        isDelete={true}
      />
    </Box>
  );
};

export default WorkflowTemplateForm;